import { validBoogDesign } from './boog-design';
import { getBoogMaterial } from './products';
import { validBoogFabricReference } from './boog-fabric-design';

export function boogFileMatchesMaterial(file: {fileName?: string; url?: string; designData?: any} | null | undefined, selected: unknown): boolean {
  if (!file?.url) return false;
  const material = getBoogMaterial(selected);
  if (file.designData?.studioFabric) return validBoogFabricReference(file.designData.studioFabric, material);
  if (file.designData?.studioDesign) return validBoogDesign(file.designData.studioDesign, material);
  // An uploaded production PDF is checked in the manual production queue.
  // Tag it with the selected material so it cannot survive a material switch.
  return /\.pdf$/i.test(file.fileName || '') && (file.designData?.studioMaterial || 'multiplex_9mm') === material;
}

/**
 * Printful-printbestanden per plaats (al geüpload, alleen https-urls). Zonder
 * dit verdwijnen na het herladen van de winkelwagen de rug- en mouwprints:
 * de order stuurt dan alleen het eerste bestand naar Printful.
 */
function persistedPrintfulDesign(value: any) {
  const printful = value?.printful;
  if (!printful || typeof printful !== 'object') return undefined;
  const files = (Array.isArray(printful.files) ? printful.files : [])
    .filter((file: any) => typeof file?.imageUrl === 'string' && file.imageUrl.startsWith('https://') && typeof file?.placement === 'string' && file.placement)
    .slice(0, 20)
    .map((file: any) => ({
      placement: file.placement,
      imageUrl: file.imageUrl,
      ...(file.printfileId != null ? { printfileId: file.printfileId } : {}),
      ...(Number.isFinite(Number(file.width)) ? { width: Number(file.width) } : {}),
      ...(Number.isFinite(Number(file.height)) ? { height: Number(file.height) } : {}),
      ...(file.printArea && typeof file.printArea === 'object' ? {
        printArea: {
          ...(Number.isFinite(Number(file.printArea.width)) ? { width: Number(file.printArea.width) } : {}),
          ...(Number.isFinite(Number(file.printArea.height)) ? { height: Number(file.printArea.height) } : {}),
        },
      } : {}),
    }));
  if (!files.length) return undefined;
  return {
    printful: {
      ...(printful.productId != null ? { productId: printful.productId } : {}),
      ...(printful.variantId != null ? { variantId: printful.variantId } : {}),
      ...(typeof printful.placement === 'string' ? { placement: printful.placement } : {}),
      files,
    },
  };
}

/** Retain compact editable Studio state on cart reload; never persist print rasters. */
export function persistedStudioDesign(value: any): any | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const readiness = value.productionReadiness?.reason === 'LOW_RESOLUTION' ? {
    productionReadiness: {
      status: 'ACTION_REQUIRED', reason: 'LOW_RESOLUTION',
      dpi: Number.isFinite(Number(value.productionReadiness.dpi)) ? Number(value.productionReadiness.dpi) : null,
      requiredDpi: Number.isFinite(Number(value.productionReadiness.requiredDpi)) ? Number(value.productionReadiness.requiredDpi) : null,
      pageWidthMm: Number.isFinite(Number(value.productionReadiness.pageWidthMm)) ? Number(value.productionReadiness.pageWidthMm) : null,
      pageHeightMm: Number.isFinite(Number(value.productionReadiness.pageHeightMm)) ? Number(value.productionReadiness.pageHeightMm) : null,
      ...(value.productionReadiness.source === 'photo' || value.productionReadiness.source === 'export' ? { source: value.productionReadiness.source } : {}),
      customerAcceptedLowResolution: value.productionReadiness.customerAcceptedLowResolution === true,
      ...(typeof value.productionReadiness.customerAcceptedAt === 'string' ? { customerAcceptedAt: value.productionReadiness.customerAcceptedAt } : {}),
    },
  } : {};
  // Een nog niet omgezet vectorbestand (EPS/PS/AI/SVG) in het ontwerp. Moet
  // een herlaad van de winkelwagen overleven, anders gaat het voorbeeld zonder
  // vasthouden naar de drukker.
  const conversionFiles = Array.isArray(value.artworkConversion?.files) ? value.artworkConversion.files : [];
  Object.assign(readiness, value.artworkConversion?.required === true ? { artworkConversion: { required: true,
    files: conversionFiles.filter((file: any) => typeof file?.url === 'string' && file.url.startsWith('https://')).slice(0, 20)
      .map((file: any) => ({ url: file.url, fileName: String(file.fileName || '').slice(0, 255), ...(typeof file.uploadId === 'string' ? { uploadId: file.uploadId } : {}) })) } } : {});
  if (validBoogFabricReference(value.studioFabric)) return {
    studioFabric: value.studioFabric, designType: 'studio-boog-fabric',
    mockupPreviewUrl: typeof value.mockupPreviewUrl === 'string' && value.mockupPreviewUrl.startsWith('https://') ? value.mockupPreviewUrl : undefined,
    printFile: value.printFile?.url ? { url: value.printFile.url, fileName: value.printFile.fileName, pageCount: value.printFile.pageCount } : undefined,
    ...readiness,
  };
  if (validBoogDesign(value.studioDesign)) {
    const result = { studioDesign: value.studioDesign, designType: 'studio-boog',
      mockupPreviewUrl: typeof value.mockupPreviewUrl === 'string' && value.mockupPreviewUrl.startsWith('https://') ? value.mockupPreviewUrl : undefined,
      printFile: value.printFile?.url ? { url: value.printFile.url, fileName: value.printFile.fileName, pageCount: value.printFile.pageCount } : undefined,
      ...readiness };
    return JSON.stringify(result).length <= 40000 ? result : undefined;
  }
  if (['multiplex_9mm', 'forex_10mm_double'].includes(value.studioMaterial)) return { studioMaterial: value.studioMaterial, ...readiness };
  // Gewone editorontwerpen: alleen de verwijzing naar de bewerkbare staat in
  // Blob. De volledige Fabric-JSON past niet betrouwbaar in localStorage.
  const printful = persistedPrintfulDesign(value) || {};
  if (typeof value.editorStateUrl === 'string' && value.editorStateUrl.startsWith('https://')) return {
    storageUrl: value.editorStateUrl,
    printFile: value.printFile?.url ? { url: value.printFile.url, fileName: value.printFile.fileName, pageCount: value.printFile.pageCount } : undefined,
    ...printful,
    ...readiness,
  };
  if (Object.keys(readiness).length || Object.keys(printful).length) return { ...printful, ...readiness };
  return undefined;
}

export function persistedStudioDesignFields(value: unknown) {
  const designData = persistedStudioDesign(value);
  return designData ? { designData } : {};
}
